import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { addSTSupplierData, addCountries, addStates } from "../../redux/action/subTierSurveyAction";
import { ReactComponent as FileDownloadIcon } from "@deere/ux.brand-foundations/icons/download.svg";
import { ReactComponent as FileUploadIcon } from "@deere/ux.brand-foundations/icons/upload.svg";
import { ReactComponent as ExcelIcon } from "@deere/ux.brand-foundations/icons/excel.svg";
import { Form } from "@deere/ux.uxframe-react";
import { Box, Grid, Card, CardContent, Typography, Button, LinearProgress } from "@deere/fuel-react";
import {
  ADDRESS_TEMPLATE_COLUMNS,
  ADDRESS_TEMPLATE_NAME,
  INVALID_FILE_ERR,
  INVALID_COUNTRY_ERR,
  OBJECT_KEYS_SUPPLIER_DATA,
  SUBTIER_TEMPLATE_POPUP,
  PRODUCT_TYPE_TOOLTIP,
} from "../../utils/surveyLiterals";
import { getCountry, getBoundary } from "../../services/external.service";
import { useOktaAuth } from "@okta/okta-react";
import { checkCountries, checkStates } from "../../utils/validationSubTier";
import { subtierExcelExport } from "../../utils/downloadUtils";

const TemplateSection = (props) => {
  const dispatch = useDispatch();
  const { authState } = useOktaAuth();
  const [loading, setLoading] = useState(false);
  const arrTableData = useSelector((state) => state.subTierSurvey.supplierData);
  const arrCountriesRx = useSelector((state) => state.subTierSurvey.countries);
  const arrStatesRx = useSelector((state) => state.subTierSurvey.states);

  let isAuthMethodOkta = props.isAuthMethodOkta || props.isAuthMethodOkta === undefined ? true : false;
  let token = props.isAuthMethodOkta || props.isAuthMethodOkta ? authState.accessToken.accessToken : "";

  const readExcel = (file) => {
    setLoading(true);
    if (file) {
      const promise = new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsArrayBuffer(file);

        fileReader.onload = (e) => {
          try {
            const bufferArray = e.target.result;
            const wb = XLSX.read(bufferArray, { type: "buffer" });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
            resolve(data);
          } catch (err) {
            setLoading(false);
            props.displayErrorCard([INVALID_FILE_ERR]); //Error when incorrect file type
          }
        };
        fileReader.onerror = (error) => {
          reject(error);
        };
      });
      promise.then((d) => {
        validateSheet(d);
      });
    }
  };

  // To check if the uploaded excel has correct format //
  const validateSheet = (data) => {
    const validHeaderArray = JSON.stringify(ADDRESS_TEMPLATE_COLUMNS);
    if (data.length > 0 && data[0].length === 1 && JSON.stringify(data[1]) === validHeaderArray) {
      props.setLoadingSuppData(true);
      formatDataObj(data.slice(2));
    } else {
      setLoading(false);
      props.displayErrorCard([INVALID_FILE_ERR]);
    }
  };

  //To Build the data Object //
  const formatDataObj = (arrData) => {
    const dataActual = [];
    arrData.forEach((data, j) => {
      let dataObj = {};
      if (data.length > 0) {
        for (let i = 0; i < OBJECT_KEYS_SUPPLIER_DATA.length; i++) {
          dataObj[OBJECT_KEYS_SUPPLIER_DATA[i]] = data[i] ? data[i].toString().trim() : "";
        }
        dataObj.id = assignIfUniqueID();
        dataObj.addedByUser = true; //Will use this as a filter for differentiating between old/new data while saving to DB
        dataActual.push(dataObj);
      }
    });
    validateCountries(dataActual);
  };

  const assignIfUniqueID = () => {
    let isUnique = true;
    let random = Math.floor(Math.random() * 10000) + 1;
    let randomID = "NewAdded_" + random;
    for (let i = 0; i < arrTableData.length; i++) {
      if (isNaN(arrTableData[i].id) && arrTableData[i].id.split("_")[1] === random) {
        isUnique = false;
        break;
      }
    }
    if (isUnique) return randomID;
    else assignIfUniqueID();
  };

  //Getting the countries for the validation
  const validateCountries = (dataExcel) => {
    const getCountries = async () => {
      let response = [];
      try {
        response = await getCountry(token, isAuthMethodOkta);
      } catch (err) {
        console.log(err);
      }
      if (response.length !== 0) {
        const [dataObject, arrStates, showErrorCard] = checkCountries(dataExcel, response);
        dispatch(addCountries(response));
        if (arrStates.length > 0) checkAvailableStates(dataObject, arrStates, showErrorCard);
        else {
          setLoading(false);
          if (showErrorCard) props.displayErrorCard([INVALID_COUNTRY_ERR]);
          dispatch(addSTSupplierData(dataObject));
        }
      } else {
        setLoading(false);
        dispatch(addSTSupplierData(dataExcel));
      }
    };
    if (arrCountriesRx.length === 0) getCountries();
    else {
      const [dataObject, arrStates, showErrorCard] = checkCountries(dataExcel, arrCountriesRx);
      if (arrStates.length > 0) checkAvailableStates(dataObject, arrStates, showErrorCard);
      else {
        setLoading(false);
        if (showErrorCard) props.displayErrorCard([INVALID_COUNTRY_ERR]);
        dispatch(addSTSupplierData(dataObject));
        props.setLoadingSuppData(false);
      }
    }
  };

  const checkAvailableStates = (dataObject, arrStates, showErrorCard) => {
    let statesAvailable = [];
    let statesToBeFetched = [];
    arrStates.forEach((i) => {
      let Rx = arrStatesRx.find((j) => i.countryCode === Object.keys(j)[0]);
      if (!Rx) statesToBeFetched.push(i);
      else {
        i.states = Rx[i.countryCode];
        statesAvailable.push(i);
      }
    });
    if (statesToBeFetched.length > 0) getStates(dataObject, statesAvailable, statesToBeFetched, showErrorCard);
    else {
      setLoading(false);
      const [showError, data] = checkStates(dataObject, statesAvailable, showErrorCard);
      if (showError) props.displayErrorCard([INVALID_COUNTRY_ERR]);
      dispatch(addSTSupplierData(data));
      props.setLoadingSuppData(false);
    }
  };

  //Getting the states for validation //
  const getStates = (dataExcel, statesAvailable, statesToBeFetched, showErrorCard) => {
    let responseCount = 0;
    let statesToDispatch = [];
    statesToBeFetched.forEach(async (cntry, i) => {
      try {
        await getBoundary(token, cntry.countryCode, isAuthMethodOkta)
          .then((response) => {
            if (response.length !== 0) {
              let temp = [];
              response.forEach((item) => temp.push(item.name));
              statesToBeFetched[i].states = [...temp];
            }
            let temp = [];
            response.forEach((item) => temp.push(item.name));
            statesToDispatch.push({ [cntry.countryCode]: [...temp] });
            responseCount++;
            if (responseCount === statesToBeFetched.length) {
              setLoading(false);
              let allStates = statesAvailable.concat(statesToBeFetched);
              const [showError, dataObject] = checkStates(dataExcel, allStates, showErrorCard);
              if (showError) props.displayErrorCard([INVALID_COUNTRY_ERR]);
              dispatch(addSTSupplierData(dataObject));
              dispatch(addStates(statesToDispatch));
            }
          })
          .catch(() => {
            responseCount++;
          });
      } catch (err) {
        console.log(err);
      }
    });
  };

  return (
    <Card borderRadius="none">
      <CardContent>
        <p>
        <span style={{ fontWeight: 600 }}>Template</span>
          <span style={{ fontWeight: 200 }}> (You can use this template if you have multiple suppliers to upload)</span>
        </p>
        <br></br>
        <Grid container justifyContent="space-around" mb={4}>
          <Grid item xs={12} md={4}>
            <Box bgcolor="white" border="1px solid grey" textAlign="center" padding={4}>
            <Typography>
              Download Sub Tier Supplier Template Here
            </Typography>
              <FileDownloadIcon fontSize="large" />
              <br />
              <Button
                variant="secondary"
                startIcon={<ExcelIcon />}
                onClick={() => {
                  subtierExcelExport(ADDRESS_TEMPLATE_NAME, ADDRESS_TEMPLATE_COLUMNS, SUBTIER_TEMPLATE_POPUP, PRODUCT_TYPE_TOOLTIP, props.catalogueData);
                }}
              >
                Address Template.xlsx
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box bgcolor="white" border="1px solid grey" textAlign="center" padding={4}>
            <Typography >
              Upload Sub Tier Supplier Template Here
            </Typography>
              <FileUploadIcon fontSize="large" />
              <br />
              <Form.Control
                type="file"
                accept=".xls,.xlsx"
                style={{ border: "1px solid lightgrey" }}
                onClick={(e) => (e.target.value = null)}
                onChange={(e) => readExcel(e.target.files[0])}
                disabled={loading}
              />
              {loading && <LinearProgress size="md" variant="indeterminate" />}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TemplateSection;
